import { useState, type FormEvent } from "react";
import { Loader2, Pizza as PizzaIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLocalization } from "@/context/localization-context";
import { errorMessage, httpStatus, signIn, storeIdentity, type StationIdentity } from "./api";

/** scope claim of the token (no verification: only to tell a station account from an admin one) */
function tokenScope(token: string): string | null {
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return typeof payload.scope === "string" ? payload.scope : null;
  } catch {
    return null;
  }
}

/** Sign-in of the tablet with its station account (tools/ops/create_station.py). */
export function LoginScreen({ onSignedIn }: { onSignedIn: (identity: StationIdentity) => void }) {
  const { t } = useLocalization();
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const identity = await signIn(login, password);
      if (tokenScope(identity.token) !== "mobile") {
        storeIdentity(null);
        setError(t("station.login.notStation"));
        return;
      }
      onSignedIn(identity);
    } catch (e) {
      const status = httpStatus(e);
      setError(
        status === 401
          ? t("station.login.invalid")
          : status === 403
            ? t("station.login.refused", { error: errorMessage(e) })
            : t("station.error.network", { error: errorMessage(e) }),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-1 items-center justify-center overflow-y-auto p-6">
      <form onSubmit={(event) => void submit(event)} className="flex w-full max-w-sm flex-col gap-5 rounded-2xl border bg-card p-6 shadow-lg">
        <div className="flex flex-col items-center gap-2 text-center">
          <PizzaIcon className="h-12 w-12 text-amber-400" aria-hidden />
          <h1 className="text-2xl font-semibold">{t("station.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("station.login.description")}</p>
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="station-login">{t("station.login.login")}</Label>
          <Input
            id="station-login"
            value={login}
            onChange={(event) => setLogin(event.target.value)}
            autoComplete="username"
            autoCapitalize="none"
            className="h-12 text-base"
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="station-password">{t("station.login.password")}</Label>
          <Input
            id="station-password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            className="h-12 text-base"
          />
        </div>
        {error && (
          <p role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
            {error}
          </p>
        )}
        <Button type="submit" className="h-12 text-base" disabled={busy || !login.trim() || !password}>
          {busy && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}
          {t("station.login.submit")}
        </Button>
      </form>
    </div>
  );
}
